import useDataGrid from "components/templates/dataGridTable";
import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import translations from "translations";
import useGetUserRoleColumns from "./useGetUserRoleColumns";
import { drawerAtom, mappingRoleUsersLoading, selectedUsersOnRoleState } from "../RecoilState/mappingUserRoleState";
import { Box, IconButton, SwipeableDrawer, Typography, styled } from "@mui/material";
import MappingRoleScopesTable from "../UserRolesManager/MappingRoleScopesTable";
import CloseIcon from "icons/components/CloseIcon";
import { useStyles } from "../FormModal/utils";
import { Buttons } from "./Buttons";
import {
  GetUsersForRoleMappingQueryVariables,
  useGetUsersForRoleMappingLazyQuery,
  GetUsersForRoleMappingQuery,
  PaginatedResultType,
} from "graphqlBase/Settings/__generated__/getUsersForRoleMapping";
import { QueryLazyOptions } from "@apollo/client";
import { useGetMappingsUsersToSave } from "../UpdateRole/useGetMappingsUsersToSave";
import useBlockHistory, { UseBlockHistoryCb } from "routes/useBlockHistory";
import FormModal from "../FormModal";

export type User = PaginatedResultType<GetUsersForRoleMappingQuery["users"]>;
type QueryOptions = QueryLazyOptions<Omit<GetUsersForRoleMappingQueryVariables, "roleIds">> | undefined;

const DropdownWrapperSC = styled("div")({
  zIndex: 999,
  backgroundColor: "#fff",
  height: "100%",
  minHeight: "40vh",
  padding: "0px 45px 0px 45px",
});
const { firstName, lastName, email } = translations.entities.user.columns;
const { label } = translations.pages.userManagement;
const nameStyle = {
  display: "flex",
  justifyContent: "center",
  marginTop: "27px",
  marginBottom: "37px",
  width: "100%",
  marginRight: "66px",
};
const drawrStyle = { marginLeft: "29px", marginRight: "29px" };
const pinnedColumns = ["firstName", "lastName", "email", "tenant"];

const UserAndRolesTable: React.FC<{}> = () => {
  const roleUsersLoading = useRecoilValue(mappingRoleUsersLoading);
  const setSelectedUsers = useSetRecoilState(selectedUsersOnRoleState);
  const resetSelectedUsers = useResetRecoilState(selectedUsersOnRoleState);
  const [openModal, setOpenModal] = useState(false);
  const { continueAfterBlock, setBlockCallBack, setPreventPageClose } = useBlockHistory();

  const variables = useMemo(() => ({}), []);

  const { closeButton } = useStyles({});
  const [queryInner, { data, loading, error, refetch }] = useGetUsersForRoleMappingLazyQuery({
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    onError: (queryError) => {
      console.error(queryError);
    },
  });

  const users = useMemo<User[]>(() => (data?.users?.items as User[]) || [], [data?.users?.items]);
  const { roles } = useGetUserRoleColumns(users);

  const query = useCallback(
    (options: QueryOptions = {}) => {
      Promise.resolve(
        queryInner({
          ...options,
          variables: options?.variables,
        })
      ).catch((queryError) => {
        console.error(queryError);
      });
    },
    [queryInner]
  );

  const { DataGrid, useMakeColumns, makeSeverSideFilter } = useDataGrid<User, "User">({
    query,
    variables,
    tableId: "MappingRoleUsersTable",
    persistance: "runTime",
  });

  const [DrawerState, setDrawerState] = useRecoilState(drawerAtom);

  const roleColumns = useMakeColumns(roles, []);

  const setRemoteDataToRecoilState = useCallback(
    (sourceUsers: User[]) => {
      const userRoleMappings = sourceUsers
        .flatMap((user) => user?.mappingRoleUsers)
        .map((userRoleMapping) => ({
          ...userRoleMapping,
          roleId: userRoleMapping?.roleId ?? "",
          userId: userRoleMapping?.userId ?? "",
          toDelete: userRoleMapping?.toDelete ?? undefined,
        }));

      setSelectedUsers((prevUserRoleMappings) => {
        return userRoleMappings.reduce((userRoleMappingsNew, userRoleMapping) => {
          if (!userRoleMapping) return userRoleMappingsNew;
          const existingMapping = userRoleMappingsNew.find((userRoleMappingNew) => {
            return (
              userRoleMappingNew.roleId === userRoleMapping.roleId &&
              userRoleMappingNew.userId === userRoleMapping.userId
            );
          });
          if (existingMapping) return userRoleMappingsNew;
          return userRoleMappingsNew.concat(userRoleMapping);
        }, prevUserRoleMappings);
      });
    },
    [setSelectedUsers]
  );

  const cancelModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const saveMappingsUsers = useGetMappingsUsersToSave();

  const doMutations = useCallback(() => {
    saveMappingsUsers().finally(() => {
      if (refetch) {
        Promise.resolve(refetch()).catch((refetchError) => {
          console.error(refetchError);
        });
      }
    });
  }, [refetch, saveMappingsUsers]);

  const cancelRoleToSave = useCallback(() => {
    resetSelectedUsers();
    setRemoteDataToRecoilState(users);
  }, [resetSelectedUsers, setRemoteDataToRecoilState, users]);

  const TriggerBlocker = useCallback(
    (block: boolean) => {
      const blockerCb: UseBlockHistoryCb = () => {
        setOpenModal(true);
        return false;
      };
      setBlockCallBack(() => (block ? blockerCb : undefined));
      setPreventPageClose(block);
    },
    [setBlockCallBack, setPreventPageClose]
  );

  useEffect(() => {
    resetSelectedUsers();
    setRemoteDataToRecoilState(users);
  }, [resetSelectedUsers, setRemoteDataToRecoilState, users]);

  const columns = useMakeColumns(
    [
      {
        field: "firstName",
        headerName: firstName,
        width: 125,
        minWidth: 150,
        maxWidth: 200,
        resizable: true,
        valueGetter: (params) => {
          return params.row.firstName ?? "";
        },
        remoteOrder: (sort) => ({ firstName: sort }),
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            firstName: filterValue,
          }),
        }),
        type: "string",
      },
      {
        field: "lastName",
        headerName: lastName,
        minWidth: 150,
        maxWidth: 200,
        resizable: true,
        remoteOrder: (sort) => ({ lastName: sort }),
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            lastName: filterValue,
          }),
        }),
        valueGetter: (params) => {
          return params.row?.lastName ?? "";
        },
        type: "string",
      },
      {
        field: "email",
        headerName: email,
        minWidth: 150,
        maxWidth: 200,
        resizable: true,
        valueGetter: (params) => {
          return params.row?.email ?? "";
        },
        remoteOrder: (sort) => ({ email: sort }),
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            email: filterValue,
          }),
        }),
        type: "string",
      },
      {
        field: "tenant",
        headerName: translations.entities.tenant.name,
        minWidth: 150,
        maxWidth: 200,
        resizable: true,
        remoteOrder: (sort) => ({ tenant: { tenantName: sort } }),
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            tenant: { tenantName: filterValue },
          }),
        }),
        valueGetter: (params) => {
          return params.row.tenant?.tenantName ?? "";
        },
        type: "string",
      },
    ],
    []
  );
  const mergedColumns = columns.concat(roleColumns);

  const tableLoading = loading || !!roleUsersLoading;

  const drawerHeader = (
    <div style={{ display: "flex" }}>
      <IconButton className={closeButton} onClick={() => setDrawerState(undefined)} size="small">
        {/* eslint-disable-next-line react/jsx-max-depth */}
        <CloseIcon />
      </IconButton>
      <Typography variant="h6" component="h2" style={nameStyle}>
        {DrawerState?.name}
      </Typography>
    </div>
  );

  const drawerBody = (
    <div>
      <Typography style={drawrStyle}>{DrawerState?.description}</Typography>
      <div style={drawrStyle}>
        {/* eslint-disable-next-line react/jsx-max-depth */}
        <MappingRoleScopesTable roleId={DrawerState?.id ?? ""} disabled />
      </div>
    </div>
  );

  return (
    <>
      <Box sx={{ pb: "80px" }}>
        <DropdownWrapperSC>
          <DataGrid
            totalCount={data?.users?.totalCount}
            noDataMessage=""
            tableTitle={label}
            tableHeight="60vh"
            disableColumnResize={false}
            columns={mergedColumns}
            initialState={{ pinnedColumns: { left: pinnedColumns } }}
            rows={!tableLoading && !error ? users : []}
            loading={tableLoading}
            checkboxSelection={false}
            withMargin={false}
          />
          <div style={{ marginLeft: "74%", marginTop: "15px" }}>
            <Buttons doMutations={doMutations} cancelAction={cancelRoleToSave} setBlocked={TriggerBlocker} />
          </div>
        </DropdownWrapperSC>
        <>
          <SwipeableDrawer
            anchor="right"
            open={!!DrawerState}
            onClose={() => setDrawerState(undefined)}
            onOpen={() => null}
          >
            {drawerHeader}
            {drawerBody}
          </SwipeableDrawer>
          <FormModal
            handleConfirm={() => continueAfterBlock?.()}
            handleCancel={cancelModal}
            isOpen={openModal}
            titles={translations.pages.userManagement.userRoleManager.discardChanges}
            message={<>{translations.pages.userManagement.userRoleManager.DiscardMessage}</>}
            confirmLabel={translations.globals.button.yes}
            cancelLabel={translations.globals.button.cancel}
          />
        </>
      </Box>
    </>
  );
};

export default UserAndRolesTable;
